import React, { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import type { LocationMapItem } from './mapTypes';

type RackOccupancy = 'empty' | 'partial' | 'full';

interface MapRackProps {
  item: LocationMapItem;
  cellPx: number;
  isSelected: boolean;
  /** Kolonun doluluk durumu: boş → yeşil, kısmen → sarı, tamamen → turuncu */
  occupancy?: RackOccupancy;
  shelfLevelCount?: number;
  onSelect: (id: string) => void;
  onDragStart: (id: string, clientX: number, clientY: number) => void;
}

// Doluluk renkleri (gövde arka planı + kenarlık + rozet)
const OCCUPANCY_COLORS: Record<RackOccupancy, { bg: string; border: string; dot: string }> = {
  empty: { bg: 'rgba(46, 125, 50, 0.78)', border: '#1b5e20', dot: '#2e7d32' },
  partial: { bg: 'rgba(245, 179, 0, 0.85)', border: '#b26a00', dot: '#f9a825' },
  full: { bg: 'rgba(239, 108, 0, 0.88)', border: '#b23c00', dot: '#e65100' },
};

const MapRack: React.FC<MapRackProps> = ({
  item, cellPx, isSelected, occupancy = 'empty', shelfLevelCount = 0, onSelect, onDragStart,
}) => {
  const colors = OCCUPANCY_COLORS[occupancy];
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 1) return;
    e.stopPropagation();
    onSelect(item.id);
    onDragStart(item.id, e.clientX, e.clientY);
  }, [item.id, onSelect, onDragStart]);

  return (
    <Box
      onMouseDown={handleMouseDown}
      sx={{
        position: 'absolute',
        left: item.gridX * cellPx,
        top: item.gridY * cellPx,
        width: (item.gridWidth || 1) * cellPx,
        height: (item.gridHeight || 1) * cellPx,
        bgcolor: colors.bg,
        border: `1px solid ${isSelected ? '#fff' : colors.border}`,
        borderRadius: '1px',
        cursor: 'move',
        zIndex: 5,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: isSelected ? '0 0 0 2px #ff9800' : 'none',
        '&:hover': { boxShadow: '0 0 0 1px #ff9800' },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 1,
          right: 1,
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: colors.dot,
          border: '1px solid rgba(255,255,255,0.85)',
          pointerEvents: 'none',
        }}
      />
      <Typography
        className="map-item-label"
        sx={{
          fontSize: '0.5rem',
          fontWeight: 700,
          color: '#fff',
          pointerEvents: 'none',
          userSelect: 'none',
          lineHeight: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          px: '1px',
        }}
      >
        {item.code.replace(/.*-/, '') || item.name.replace(/.*\s/, '')}
      </Typography>
      {shelfLevelCount > 0 && (
        <Typography
          sx={{
            position: 'absolute',
            bottom: 1,
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '0.45rem',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.9)',
            pointerEvents: 'none',
            lineHeight: 1,
          }}
        >
          {shelfLevelCount}↕
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(MapRack);
